// Updated useMeetForm.js
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { pointsRulesProblems } from 'utils/pointsRules';

const useMeetForm = (initialValues) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // added toast errors so that the user can see the errors easier
  // seasonBestPoints: the organisation is offered season-best points (a school is not).
  // relaysOffered: it is offered relay points (it has houses).
  const validateForm = ({ seasonBestPoints = true, relaysOffered = false } = {}) => {
    const newErrors = {};

    if (!formValues.meetName?.trim()) {
      toast.error('Meet name is required');
      newErrors.meetName = 'Meet name is required';
    }

    if (!formValues.date) {
      toast.error('Date is required');
      newErrors.date = 'Date is required';
    }

    if (!formValues.time) {
      toast.error('Time is required');
      newErrors.time = 'Time is required';
    }

    if (!formValues.meetType) {
      toast.error('Meet type is required');
      newErrors.meetType = 'Please select the meet type';
    }

    // Validate Program Data
    const programDataError = validateProgramData();
    if (programDataError) {
      toast.error(programDataError);
      newErrors.programData = programDataError;
    }

    if (!formValues.location) {
      toast.error('Location is required');
      newErrors.location = 'Location is required';
    }

    if (!formValues.meetStatus) {
      toast.error('Meet status is required');
      newErrors.meetStatus = 'Please select the status';
    }

    // The meet's own points: the same checks as the server's (utils/pointsRules.js). Each problem
    // shows in the points summary on the form.
    if (formValues.useCustomPoints && formValues.customPointsRules) {
      const pointsProblems = pointsRulesProblems(formValues.customPointsRules, {
        seasonBestPoints,
        relaysOffered,
      });
      if (pointsProblems.length > 0) {
        // Doug's words, 2026-10-25 (as Club Settings' message, H6).
        toast.error("Please fix this meet's points settings before saving: see Points and Season Bests.");
        newErrors.customPointsRules = pointsProblems[0];
      }
    }

    // Meet signup window (§6.8) — only multi-club meets with self-service enabled. Both dates are required, and the
    // close must be strictly after the open (using the wall-clock defaults: open 00:00, close 23:59 when no time set).
    if (
      parseInt(formValues.meetType, 10) === 2 &&
      (formValues.selfServiceRegistrationEnabled || formValues.publicSignupEnabled)
    ) {
      if (!formValues.signupOpenDate) {
        toast.error('Registration open date is required when self-service signup is on');
        newErrors.signupOpenDate = 'Open date is required';
      }
      if (!formValues.signupCloseDate) {
        toast.error('Registration close date is required when self-service signup is on');
        newErrors.signupCloseDate = 'Close date is required';
      }
      if (formValues.signupOpenDate && formValues.signupCloseDate) {
        const open = new Date(`${formValues.signupOpenDate}T${formValues.signupOpenTime || '00:00'}`);
        const close = new Date(`${formValues.signupCloseDate}T${formValues.signupCloseTime || '23:59'}`);
        if (close <= open) {
          toast.error('Registration close must be after the open date/time');
          newErrors.signupCloseDate = 'Close must be after open';
        }
      }
    }

    return newErrors;
  };

  const validateProgramData = () => {
    if (!formValues.programData) {
      return 'Please configure event program';
    }
    if (
      formValues.programData.programGroups.some((group) => !group.sourceGroupID)
    ) {
      return 'Please select athlete group in the event program';
    }

    // Duplicates are detected by identity (source group + gender), not display
    // name — names can drift when the club age group is renamed
    // (age-group-lifecycle-design.md §7.1).
    const identityKeys = formValues.programData.programGroups.map(
      (group) =>
        `${group.groupSource}|${group.sourceGroupID}|${group.gender || 'Mixed'}`
    );
    if (new Set(identityKeys).size !== identityKeys.length) {
      return 'Please remove duplicate athlete groups';
    }

    if (
      formValues.programData.programGroups.some((group) => {
        return group.events.some((e) => !e.eventType);
      })
    ) {
      return 'Please select event type in all events';
    }

    // Locatyion is optional - remove this from validation
    // if (
    //   formValues.programData.programGroups.some((group) => {
    //     return group.events.some((e) => !e.location);
    //   })
    // ) {
    //   return 'Please select the location in all events';
    // }
    return null;
  };

  const handleInputChange = useCallback((e) => {
    setFormValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleRadioChange = (name, value) => {
    setFormValues((prev) => {
      if (prev[name].includes(value)) {
        return {
          ...formValues,
          [name]: prev[name].filter((v) => v !== value),
        };
      } else {
        return {
          ...formValues,
          [name]: [...prev[name], value],
        };
      }
    });
  };

  const handleTimeChange = (time) => {
    setFormValues({ ...formValues, time });
  };

  return {
    formValues,
    setFormValues,
    errors,
    setErrors,
    validateForm,
    handleInputChange,
    handleRadioChange,
    handleTimeChange,
    loading,
    setLoading,
  };
};

export default useMeetForm;
